/*
Description: This is the file that will handle interacting with the WebPT EMR SOAP 2.0
Date: 3/6/2024
Author: Jacob Michalski

Notes:
   All Injectors need the following functions:
       getPageInfo
       getPatientInfo
       getQuestions
       answerQuestions (Consider combining these and making the single a private function)
       answerQuestion

TODO:
   DONE Fix issue where if the section is already being shown, it will not be filled...
   DONE Fix the Mad Scroll (Applicable for Prompt too)
   Race Condition when answers come in
   Fix the thing where section titles are including last updated on XX/XX
   Verify Other Types of Notes
   Add More than just extended response questions   
   Sleep Faster
*/

import { getCapturedNotes, getNoteCacheKey } from './NoteStore.ts';
import { isNoteCaptureEnabled } from './NoteCapture.ts';

export { getSchedule } from './WebPTInject.ts';

export function getPageInfo() {
  // function getPageInfo() {
  /*
     Description: This function will get the page info from the note
     Return: A dictionary with the page info
         {
             "URL": Base URL for the page,
             "Note Type": "PT Daily/SOAP Note",...
         }
     */
  const baseURL = window.location.origin;
  const noteType = document.querySelector('legend#ta-note-title').textContent;
  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

export function getPageInfoForCapture() {
  try {
    const pageInfo = getPageInfo();
    return pageInfo['Note Type'] ? pageInfo : null;
  } catch (_) {
    return null;
  }
}

export function getPatientInfo() {
  /*
     Description: This function will get the patient info from the note
     Return: A dictionary with the patient info
     {
         "EMR ID": 0x1234 or "michalskij"
         "First Name":firstName,
         "Last Name": lastName,
         "DOB": dob,
     }
     */

  // Assuming the element's text is "Halper John, he/him (EMR ID: 42332797)"
  let patientInfoElement = document.querySelector('div.patient--name');

  const patientInfoText = patientInfoElement?.textContent;
  // const regex = /^([A-Za-z]+) ([A-Za-z]+),*.*\(EMR ID: (\d+)\)$/;
  const regex = /^([A-Za-z". -]+) ([A-Za-z" -]+),*.*\(EMR ID: (\d+)\)$/; // Old one was broken for the following "JENNIFER "AMH-EW" WRIGHT (EMR ID: 1231231)"
  // There was an issue where is someone's name started with a prefix with a . it would break...
  // const patientInfoText = "Ms. Debora "Debbie" A. Smith (EMR ID: 1231231)" so we added a . to the first sequence

  const match = patientInfoText?.match(regex);
  if (!match) {
    console.error('No match found');
    return null;
  }

  // Got the Goods
  const firstName = match[1];
  const lastName = match[2];
  const emrID = match[3];
  const dob = document
    .querySelector('span.patient--date-of-birth')
    ?.textContent?.split(' ')[0];

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

// A throw here would kill the whole capture tick, so never let one escape.
export function getPatientInfoForCapture() {
  try {
    return getPatientInfo();
  } catch (_) {
    return null;
  }
}

/**
 * Return a stable key for the open note. Each EMR derives this differently, but
 * the same note must always return the same key and two notes must not share
 * one. WebPT 2.0 routes the note editor at
 * /patient-chart/v1/patients/<id>/cases/<id>/notes/edit/<note guid>, and the
 * guid alone identifies the note.
 *
 * Returns null anywhere else (schedule, view-only note, unsaved new note) so
 * the capture poller skips the tick.
 */
export function getNoteKeyForCapture() {
  const match = window.location.pathname.match(
    /\/notes\/edit\/([0-9a-f-]{16,})/i
  );
  return match ? `note:${match[1]}` : null;
}

/*
The Sign button ("ta-" is WebPT's test-automation hook prefix):

  <button class="... ta-primary-button"><span class="ta-sign-button">Sign</span></button>

The poller matches with event.target.closest(), and a click on the label targets
the span while one on the button's padding targets the button — so match either.

This is a best-effort signal, not proof: the click can still be cancelled at a
"Confirm Date of Service" dialog, a supervising-therapist prompt, or a MIPS
waiver. That's tolerable because sign-off no longer closes a note to capture —
the next poll corrects a premature mark, and the backend upserts.
*/
export const noteFinalizationSelectorForCapture =
  '.ta-primary-button:has(.ta-sign-button), .ta-sign-button';

/*
Note Capture accumulator.

getQuestions cannot run on a timer here: it clicks every section open, opens
BlueprintJS dropdown portals, and raises a full-page blur overlay, taking
seconds. Most note data isn't even in the DOM until a section is in edit mode,
because React unmounts collapsed sections' form controls.

So capture keeps its own accumulator instead, fed only by cheap read-only sweeps
of whatever the app currently has mounted. getQuestionsForCapture then just
returns a snapshot. Keys mirror the panel scrape's three-level shape
(top -> mid -> field) so postWriteQuestions baselines intersect correctly.
*/
type CaptureCache = Record<string, Record<string, Record<string, string>>>;
let captureCache: CaptureCache = {};
let captureCacheNoteKey: string | null = null;
let captureCacheHydrated = false;

// Both the poll and the panel scrape's seed hook can be the first to touch a
// note, so resetting is shared rather than living in either one.
function resetCaptureCacheIfNoteChanged(noteKey: string) {
  if (captureCacheNoteKey === noteKey) return;
  captureCacheNoteKey = noteKey;
  captureCache = {};
  captureCacheHydrated = false;
}

function setCaptureValue(
  top: string,
  mid: string,
  field: string,
  value: string | null,
  allowNewEmpty = false
) {
  if (!top || !mid || !field || value === null) return;
  // Empty values matter only after a field has been observed: they mean the
  // clinician cleared captured content. Ignore untouched empty template fields.
  if (
    value === '' &&
    !allowNewEmpty &&
    captureCache[top]?.[mid]?.[field] === undefined
  ) {
    return;
  }
  // Live sweeps only create paths alongside a real value, so they can't grow an
  // empty top-level section. Hydration may restore an explicit stored clear.
  if (!captureCache[top]) captureCache[top] = {};
  if (!captureCache[top][mid]) captureCache[top][mid] = {};
  captureCache[top][mid][field] = value;
}

/**
 * Read every mounted note field and merge the values into captureCache.
 *
 * Strictly read-only: no clicks, no focus/blur, no scrolling, and never any of
 * the scrape's expensive helpers (extractDropdownChoices, openDropdown,
 * openCarryForward, handleFlowsheetSection). Each branch is isolated so one
 * malformed section can't cost us the rest of the note, and none of them call
 * getLogFromInjector — that ships the whole page, which must not happen on a
 * poll.
 */
function sweepIntoCaptureCache() {
  sweepFormControlsIntoCaptureCache();
  sweepFlowsheetIntoCaptureCache();
  sweepChargeSummaryIntoCaptureCache();
}

function sweepFormControlsIntoCaptureCache() {
  try {
    const noteType = getPageInfoForCapture()?.['Note Type'];
    const processedRadioGroups = new Set<Element>();
    // Duplicate labels take a " (2)" suffix allocated per mid-section in
    // document order — the same scheme parseFieldBox uses.
    const usedFieldKeys = new Map<string, Set<string>>();
    const observed = {};
    // getTopLevelSection is expensive; resolve once per edit-group.
    const topByGroup = new Map<Element, string>();

    for (const el of getFilteredFormElements()) {
      // Owned by the two branches below, which key them differently.
      if (el.closest('#root_flowsheet_activities')) continue;
      if ((el.id || '').startsWith('root_charge_summary')) continue;

      const label = findFieldLabel(el);
      if (isSkippedCaptureFieldLabel(label, noteType)) continue;

      if (el.matches('input[type="radio"]')) {
        const group = el.closest('.field-radio-group');
        if (!group || processedRadioGroups.has(group)) continue;
      }
      const value = readCaptureValueFromElement(el, processedRadioGroups);

      // F1: anchor on fieldset.edit-group, NOT the .edit-toggle wrapper — the
      // wrapper is absent while a section is in edit mode, which is the only
      // state in which its fields are mounted for us to read at all.
      const midFieldset = el.closest('fieldset.edit-group');
      if (!midFieldset) continue;
      const isAssessmentSummary =
        el.id === 'root_assessment_assessment_comments';
      const mid = isAssessmentSummary
        ? 'Assessment - Section'
        : sanitizeFieldName(
            sanitizeLegendText(midFieldset.querySelector('legend'))
          );

      if (!topByGroup.has(midFieldset)) topByGroup.set(midFieldset, getTopLevelSection(el));
      const top = topByGroup.get(midFieldset);
      // Detached mid-re-render; the next tick re-reads it.
      if (!top || top === 'Unknown Section') continue;

      const midPath = `${top} / ${mid}`;
      if (!usedFieldKeys.has(midPath)) usedFieldKeys.set(midPath, new Set());
      const used = usedFieldKeys.get(midPath);
      let field = isAssessmentSummary
        ? 'Assessment - Section'
        : sanitizeFieldName(label);
      let counter = 2;
      while (used.has(field)) field = `${sanitizeFieldName(label)} (${counter++})`;
      used.add(field);

      if (!observed[top]) observed[top] = {};
      if (!observed[top][mid]) observed[top][mid] = {};
      observed[top][mid][field] = value;
    }
    // Include empty controls in collision checks before the cache drops empty values.
    for (const [top, groups] of Object.entries(observed)) {
      for (const [mid, fields] of Object.entries(groups)) {
        for (const [field, value] of Object.entries(cleanSubsectionNames(fields, top, mid))) {
          setCaptureValue(top, mid, field, value as string | null);
        }
      }
    }
  } catch (_) {
    return;
  }
}

// Mirrors the junk-label filter in parseFieldBox; keep the two in step.
function isSkippedCaptureFieldLabel(label, noteType) {
  if (!label || label.includes('Unknown')) return true;
  if (label.startsWith('Therapy - Visit')) return true;
  if (label.includes('Date - Of - Injury - Onset')) return true;
  if (label.includes(' Medical - History - Allergies')) return true;
  if (label.endsWith('Current - Conditions')) return true;
  if (label.endsWith('Previous - Conditions 2')) return true;
  if (
    label === 'Section' &&
    (noteType === 'Initial Evaluation' || noteType === 'Progress Note')
  ) {
    return true;
  }
  return false;
}

function readCaptureValueFromElement(el, processedRadioGroups) {
  if (el.matches('input[type="radio"]')) {
    // One value per group, keyed off whichever member comes first in document
    // order — the allocation handleSubQuestion makes via processedRadioGroups.
    const group = el.closest('.field-radio-group');
    if (!group || processedRadioGroups.has(group)) return null;
    processedRadioGroups.add(group);
    const checked = group.querySelector('input[type="radio"]:checked');
    if (!checked) return '';
    const span = checked.closest('label')?.querySelector('span');
    return getExistingNote(span?.textContent || checked.value);
  }

  // Multi-selects keep their chosen values as tags, not in the input.
  if (el.classList?.contains('pt-input-ghost')) {
    const tagInput = el.closest('.pt-tag-input');
    const tags = Array.from(tagInput?.querySelectorAll('.pt-tag') || [])
      .map((tag: Element) => (tag.textContent || '').trim())
      .filter(Boolean);
    // '$'-joined matches the format the AI answer path reads and writes.
    return tags.join('$');
  }

  if (el.tagName === 'SELECT') {
    // The option label, not the submit value, is what a reader needs.
    const selected = el.options?.[el.selectedIndex];
    if (!el.value) return '';
    return getExistingNote(selected?.textContent || el.value);
  }

  return typeof el.value === 'string' ? el.value.trim() : null;
}

// Read-only twin of handleFlowsheetSection's row walk. Deliberately not a call
// into it: that one rebuilds questionInteractions and would swap the Input
// closures out from under an in-flight AI write.
function sweepFlowsheetIntoCaptureCache() {
  try {
    const usedNames = new Set<string>();
    const rows = document.querySelectorAll(
      'table#root_flowsheet_activities_activity_list tbody tr'
    );

    rows.forEach((row, index) => {
      const nameEl = row.querySelector('.activity-list--activity-name div');
      const activityName =
        nameEl?.textContent?.trim() || `Activity ${index + 1}`;
      if (!nameEl || !activityName) return;

      const base = sanitizeFieldName(activityName);
      let mid = base;
      let counter = 2;
      while (usedNames.has(mid)) mid = `${base} (${counter++})`;
      usedNames.add(mid);

      row.querySelectorAll('input, textarea').forEach((input: any) => {
        const cell = input.closest('td');
        const match = (cell?.getAttribute('class') || '').match(
          /activity-list--([a-zA-Z-]+)/
        );
        if (!match) return;

        const raw = match[1].replace(/-/g, ' ').trim();
        const field = sanitizeFieldName(
          raw.charAt(0).toUpperCase() + raw.slice(1)
        );
        const value =
          input.type === 'checkbox'
            ? input.checked
              ? 'Yes'
              : ''
            : typeof input.value === 'string'
              ? input.value.trim()
              : null;

        setCaptureValue('Flowsheet', mid, field, value);
      });
    });

    const comments: any = document.getElementById(
      'root_flowsheet_activities_additional_comments'
    );
    if (comments) {
      const commentValue =
        typeof comments.value === 'string' ? comments.value.trim() : null;
      setCaptureValue(
        'Flowsheet',
        'Additional Comments',
        'Additional Comments',
        commentValue
      );
    }
  } catch (_) {
    return;
  }
}

// Keyed by CPT code, the same way parseCodingJustification does it.
function sweepChargeSummaryIntoCaptureCache() {
  try {
    document
      .querySelectorAll('textarea[id^="root_charge_summary"]')
      .forEach((textarea: any) => {
        const code = lookUpForSiblingElement(
          textarea,
          '.pt-button-text'
        )?.textContent?.trim();
        const value =
          typeof textarea.value === 'string' ? textarea.value.trim() : null;
        if (!code) return;
        setCaptureValue(
          'Charge Summary',
          'Charges',
          sanitizeFieldName(code),
          value
        );
      });
  } catch (_) {
    return;
  }
}

/**
 * Return current note values keyed by question key: top-level section, then mid
 * section, then field. Only sections the user (or a panel scrape) has opened are
 * present — the values accumulate as the note is worked on.
 */
export async function getQuestionsForCapture(): Promise<Record<string, any>> {
  const noteKey = getNoteKeyForCapture();
  if (!noteKey) return {};

  resetCaptureCacheIfNoteChanged(noteKey);
  if (!captureCacheHydrated) {
    captureCacheHydrated = true;
    await hydrateCaptureCacheFromStore(noteKey);
  }

  if (
    captureCacheNoteKey !== noteKey ||
    getNoteKeyForCapture() !== noteKey
  ) {
    return {};
  }

  sweepIntoCaptureCache();
  return JSON.parse(JSON.stringify(captureCache));
}

/**
 * Fill the cache in from whatever is already stored for this note.
 *
 * After a page reload or an SPA switch away and back, the module cache starts
 * empty, so a snapshot would only describe the sections open right now. NoteStore
 * merges top-level keys shallowly, so saving that would *replace* an accumulated
 * section subtree with the partial one.
 *
 * Only fills gaps: anything already swept or seeded in this page session is
 * newer than the store and must win.
 */
async function hydrateCaptureCacheFromStore(noteKey: string) {
  try {
    const store = await getCapturedNotes();
    if (captureCacheNoteKey !== noteKey) return;
    // 'emr.webpt.com' is lookupInjectorKey()'s value for this injector, which is
    // the emrKey the poller saves under.
    const entry = store?.entries?.[getNoteCacheKey('emr.webpt.com', noteKey)];
    const stored = entry?.questions;
    if (!stored || typeof stored !== 'object') return;

    Object.entries(stored).forEach(([top, mids]) => {
      if (!mids || typeof mids !== 'object') return;
      Object.entries(mids).forEach(([mid, fields]) => {
        if (!fields || typeof fields !== 'object') return;
        Object.entries(fields).forEach(([field, value]) => {
          if (typeof value !== 'string') return;
          if (captureCache[top]?.[mid]?.[field] !== undefined) return;
          setCaptureValue(top, mid, field, value, true);
        });
      });
    });
  } catch (_) {
    return;
  }
}

/**
 * Harvest the values a real panel scrape just revealed.
 *
 * The click-driven scrape opens everything, so it sees fields no passive sweep
 * can reach. Piggy-backing on it is free coverage.
 *
 * Reads 'Existing Value' leaves only. Note the question-level ExistingNote is
 * deliberately ignored: the assembly loop sets it to '' on every question, and
 * treating that as a value would put a string where the cache expects a section.
 */
function seedCaptureCacheFromScrape(scrapedQuestions, expectedNoteKey) {
  const noteKey = getNoteKeyForCapture();
  if (
    !noteKey ||
    noteKey !== expectedNoteKey ||
    !Array.isArray(scrapedQuestions)
  ) {
    return;
  }

  resetCaptureCacheIfNoteChanged(noteKey);

  scrapedQuestions.forEach((question) => {
    const top = question?.Key;
    if (!top || question?.Type !== 'Flex' || !question?.Structure) return;

    Object.entries(question.Structure).forEach(([mid, fields]) => {
      // prune_early is a boolean marker the assembly loop adds, not a section.
      if (mid === 'prune_early' || !fields || typeof fields !== 'object') return;

      Object.entries(fields).forEach(([field, meta]: [string, any]) => {
        const value = meta?.['Existing Value'];
        if (typeof value !== 'string' || !value) return;
        setCaptureValue(top, mid, field, value);
      });
    });
  });
}

/*
questions - This is where we will store the questions
         - a list of dictionaries
    [{
       "Question": "Where was the injury?", 
       "Type": "Multiple Choice", 
       "Choices": ["Back", "Neck", "Shoulder"]
    },
       ...
       ]
 
*/
var questions = [];
var questionInteractions = {};
// Cache the computed questions to avoid expensive recomputation for rapid, back-to-back calls
var cachedQuestions = null;
var lastGetQuestionsAt = 0;

var dropdownMisses = {};
const SKIP_VALUES = new Set(['', 'null', 'none', 'other', 'not tested']);
/*
question Interactions - This is where we will store the interactions for each question
                       - a dictionary of dictionaries
        {
           "Where was the injury?": {
               "Type": "Multiple Choice",
               "Choices"{
                   "Back": () => {click the back button},
                   "Neck": () => {click the neck button},
                   "Shoulder": () => {click the shoulder button}
               }
           },
       },
       {
           "What is the injury?": {
               "Type": "Short Answer",
               "Input": (val) => {input the value into the input box}
       }
           ...
        }
*/

async function answerShortAnswer(
  localQuestionSelector,
  localSubSectionSelector,
  val,
  replyType
) {
  // console.log('Local Question Selector: ' + localQuestionSelector);
  // console.log('Local Sub Section Selector: ' + localSubSectionSelector);
  // console.log('Value: ' + val);
  // console.log('Reply Type: ' + replyType);
  if (!val || SKIP_VALUES.has(val.trim().toLowerCase())) {
    return;
  }
  try {
    // Only click to expand the section if the target element is not already visible.
    // Clicking an already-open section toggles it closed, causing subsequent fills to fail.
    const localSubQuestion = document.querySelector(localQuestionSelector);
    if (localSubQuestion) {
      // This is the most efficient place to wait as the hide of another sub section to show this
      // one is where data will get lost...
      await sleep(250);
      localSubQuestion.click();
      await sleep(10);
    }
    const localSubQuestionTextArea = document.querySelector(
      localSubSectionSelector
    );

    // Actually Type
    if (replyType === 'append') {
      return setReactTextareaValue(
        localSubQuestionTextArea,
        localSubQuestionTextArea.value + '\n' + val
      );
    } else {
      return setReactTextareaValue(localSubQuestionTextArea, val);
    }
  } catch (error) {
    console.error('Error in answerShortAnswer:', error);
    getLogFromInjector();
  }
  return null;
}

function decodeHtmlEntities(text: string): string {
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

// React can be a bit difficult to bubble up changes but this works
async function setReactTextareaValue(textarea, value) {
  // Decode HTML entities (e.g. &ldquo; → ", &rsquo; → ', &nbsp; → space)
  // so they don't appear as raw entity strings in the finalized WebPT note.
  if (typeof value === 'string') {
    value = decodeHtmlEntities(value);
  }

  // Filter null or empty values
  if (!value || SKIP_VALUES.has(value.trim().toLowerCase())) {
    return;
  }

  // Get the property descriptor for the 'value' property of the HTMLTextAreaElement prototype
  try {
    if (!textarea) {
      console.warn('Textarea not found');
      return;
    }

    // Set the value using the native value setter

    const proto = Object.getPrototypeOf(textarea);
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;

    if (!setter) {
      console.warn('Could not get setter for textarea value');
      return;
    }

    setter.call(textarea, value);

    return new Promise((resolve) => {
      try {
        // We need some small amount of time to make sure the event is actually triggered... I tried 100ms and it didn't work consistently...
        textarea.addEventListener(
          'input',
          () => {
            setTimeout(resolve, 200);
          },
          { once: true }
        );
        // Dispatch the 'input' event to trigger React's onChange handler

        const event = new Event('input', { bubbles: true });
        textarea.dispatchEvent(event);
      } catch (eventError) {
        console.error('Failed to dispatch input event:', eventError);
        resolve(); // Resolve to avoid blocking execution
      }
    });
  } catch (err) {
    console.error('Error in setReactTextareaValue:', err);
    return;
  }
}

function getSelector(elm) {
  // This function will return the selector for a given element
  // Useful when the element reference may be deleted and recreated in the DOM
  if (!elm || !elm.tagName) {
    console.warn('Invalid element provided to getSelector');
    return '';
  }

  if (elm.tagName === 'BODY') return 'BODY';
  const names = [];
  while (elm?.parentElement && elm.tagName !== 'BODY') {
    if (elm.id) {
      names.unshift('#' + elm.getAttribute('id')); // Use getAttribute to ensure raw value
      break; // ID is unique, we don't need more
    } else {
      let c = 1,
        e = elm;
      while (e.previousElementSibling) {
        e = e.previousElementSibling;
        c++;
      }
      names.unshift(elm.tagName + ':nth-child(' + c + ')');
    }
    elm = elm.parentElement;
  }
  return names.join('>');
}

/**
 * Sanitizes field names to be safe for JSON schema property names
 * Removes characters that are not allowed in structured output schemas
 * (e.g., double quotes, backslashes, trailing escape characters)
 */
function sanitizeFieldName(fieldName) {
  if (!fieldName || typeof fieldName !== 'string') {
    return fieldName || 'Unknown';
  }
  let sanitized = fieldName
    // Remove double quotes (not allowed in JSON schema property names)
    .replace(/"/g, '')
    // Remove backslashes (escape characters that can cause issues)
    .replace(/\\/g, '')
    // Remove trailing escape characters (e.g., trailing backslash)
    .replace(/\\+$/, '')
    // Remove any control characters
    .replace(/[\x00-\x1F\x7F]/g, '')
    // Trim whitespace
    .trim();
  return sanitized || 'Unknown';
}

function cleanSubsectionNames(fields, top, parent) {
  if (top !== 'Plan') return fields;
  const words = parent.split(/[\s/]+/).filter(Boolean);
  const prefix = `${[top, ...words].join(' - ')} - `;
  const original = Object.keys(fields);
  const cleaned = original.map((name) => name.toLowerCase().startsWith(prefix.toLowerCase())
    ? name.slice(prefix.length) : name);
  const unsafe = [parent, ...original, ...cleaned].some((name) => !name || name.replace(/["']/g, '').trim() !== name);
  const collision = cleaned.some((name, index) =>
    cleaned.indexOf(name) !== index || (name !== original[index] && original.includes(name)));
  if (unsafe || collision) return fields;
  return Object.fromEntries(original.map((name, index) => [cleaned[index], fields[name]]));
}

function sanitizeLegendText(legendEl) {
  if (!legendEl) {
    return 'Unknown Section';
  }
  let text = legendEl.textContent || '';
  // Remove inner last documented/date fragments if present
  const lastDocEl = legendEl.querySelector?.('.last-documented-date');
  if (lastDocEl?.textContent) {
    text = text.replace(lastDocEl.textContent, '');
  }
  // Remove trailing "Last updated/Last documented ..." tails
  text = text.replace(/\bLast\s+(updated|documented)[\s\S]*$/i, '');
  // Remove standalone trailing dates like mm/dd/yyyy or m/d/yy
  text = text.replace(/\(?\s*\b\d{1,2}\/\d{1,2}\/\d{2,4}\b\s*\)?\s*$/i, '');
  // Sanitize the field name to remove problematic characters
  return sanitizeFieldName(text.trim());
}

function getFilteredFormElements(): Element[] {
  return Array.from(
    document.querySelectorAll(
      'textarea.form-control, input[type="text"], input[type="number"], input[type="radio"], select, .pt-input, .pt-input-ghost'
    )
  ).filter((el) => {
    // Skip any element inside the "Visit" section or non-question fields
    const id = el.id || '';
    const isVisitField = el.closest('#root_therapy_visit');
    return (
      !isVisitField &&
      ![
        'root_therapy_visit_date_of_visit',
        'root_therapy_visit_place_of_service'
      ].includes(id)
    );
  });
}

function getTopLevelSection(element) {
  // This function will get the top level section of a given element
  // This is the Subjective, Objective, Assessment, Plan, etc.
  let maxDepth = 10; // Prevent infinite loop
  let currentElement = element;

  while (currentElement && currentElement.parentElement && maxDepth > 0) {
    if (currentElement.querySelector('legend[class*="schema-path-depth-1"]')) {
      return sanitizeLegendText(currentElement.querySelector('legend'));
    }
    currentElement = currentElement.parentElement;
    maxDepth--;
  }

  // If we couldn't find the legend, return a fallback
  console.warn('Could not find top level section legend');
  return 'Unknown Section';
}

export async function getQuestions(
  isWebPTUser,
  dropdownCache = {},
  replyType = {}
) {
  // Return cached results if the last successful computation was within 10 seconds
  const now = Date.now();
  if (cachedQuestions && now - lastGetQuestionsAt < 10000) {
    return cachedQuestions;
  }
  // If we're actively answering questions, don't re-scrape — return cache regardless
  // of the 10s window. Cache will be invalidated when each question finishes.
  if (cachedQuestions && __overlayAnswerQueue.length > 0) {
    return cachedQuestions;
  }

  const captureNoteKeyAtScrapeStart = getNoteKeyForCapture();

  // Begin overlay (ref-counted) for the scrape
  beginOverlay(
    'Finding sections we can complete for you. This won’t take long..',
    { title: 'ComprehendPT' }
  );
  try {
    questions = [];
    questionInteractions = {};

    // Scroll back to this point after we scrape
    const scrollElement = document.querySelector('#note-editor-scroll');
    let scrollTop = scrollElement.scrollTop;

    // At this point each of the q's are the mid level section where Top Level Section is Subjective, mid level would be Subjective - Patient Presentation
    // The goal is to Change the final structure from 90 seperate Q's to Just SOAP, but an incremental is to get each of the mid level sections
    const qs = document.querySelectorAll(
      '.edit-toggle > fieldset > .fields-box'
    );

    /* Start Mid Level Section Parsing That's the Flex for Patient Presentation, Past Medical History, etc. and inside, each of it's text areas...
       For each of the Mid Level Sections, get the infractions there as flex objects
       We are doing this in a local function because if any section were open, we don't get them on this first pass and will need to reuse it later...
       */

    // Snapshot section identifiers now, before any async iteration detaches nodes.
    // Async processing of earlier sections triggers React re-renders that detach qs[i].
    // When that happens getTopLevelSection returns "Unknown Section" — the snapshot
    // is consulted only in that case, so every other section is completely unaffected.
    const _sectionSnapshot = Array.from(qs).map((q: Element) => ({
      topLevel: getTopLevelSection(q),
      midLevelKey: sanitizeLegendText(
        (q as HTMLElement).parentElement?.querySelector('legend')
      )
    }));

    async function parseMidLevelSection(qs, i, dropdownCache, replyType) {
      let topLevelSection = getTopLevelSection(qs[i]);
      if (topLevelSection === 'Unknown Section') {
        topLevelSection = _sectionSnapshot[i]?.topLevel || 'Unknown Section';
      }
      if (!questionInteractions[topLevelSection]) {
        questionInteractions[topLevelSection] = {
          Type: 'Flex',
          Key: topLevelSection,
          Question: topLevelSection,
          Structure: {}
        };
      }

      if (
        replyType &&
        replyType.TopLevelReplyType &&
        replyType.TopLevelReplyType[topLevelSection] === 'no_reply'
      ) {
        console.log('Skipping Section: ', topLevelSection);
        return;
      }
      // Mid Level Section Key (This is Patient Presentation, Past Medical History, etc.)
      let midLevelSectionKey = qs[i].isConnected
        ? sanitizeLegendText(qs[i].parentElement.querySelector('legend'))
        : _sectionSnapshot[i]?.midLevelKey;
      midLevelSectionKey = sanitizeFieldName(midLevelSectionKey);

      // Get the Actual Interactable Flex Object for this Mid Level Section
      let midLevelSectionInteractions = await parseFieldBox(
        qs[i],
        dropdownCache,
        replyType
      );

      // Add this mid Level Section to the Top Level Section as flex (Only if it's not null)
      if (midLevelSectionInteractions) {
        // For "Activities/Interventions", do nothing because it's handled separately
        if (midLevelSectionKey === 'Activities/Interventions') {
          console.log('Activities/Interventions found');
        } else {
          questionInteractions[topLevelSection].Structure[midLevelSectionKey] =
            cleanSubsectionNames(midLevelSectionInteractions, topLevelSection, midLevelSectionKey);
        }
      }
    }

    // For each of the Mid Level Sections, get the infractions there as flex objects
    for (var i = 0; i < qs.length; i++) {
      // If any one of the questions fail, don't bring the whole thing down...
      try {
        await parseMidLevelSection(qs, i, dropdownCache, replyType);
      } catch (error) {
        console.error('Error in Mid Level Section Parsing:', error);
        getLogFromInjector();
      }
    }

    // If the user was sitting in a field when we did the first query selector, we didn't detect it
    // Instead of not getting it, we will recall the query selector and check for any new questions (This is what I was talking about in the local function)
    const newQs = document.querySelectorAll(
      '.edit-toggle > fieldset > .fields-box'
    );
    // Loop over the new Qs and find the elements that are not in the old Qs
    // If on the retry we fail, don't bring the whole thing down...
    try {
      for (var i = 0; i < newQs.length; i++) {
        let found = false;
        for (var j = 0; j < qs.length; j++) {
          if (getSelector(newQs[i]) === getSelector(qs[j])) {
            found = true;
            break;
          }
        }
        if (!found) {
          // TODO, this is a Flex, so throw it in where it belongs.
          await parseMidLevelSection(newQs, i, dropdownCache, replyType);
        }
      }
    } catch (error) {
      console.error(
        'Error in Mid Level Section Parsing of New Questions:',
        error
      );
      getLogFromInjector();
    }

    // Plan Comments: close siblings, open section, wait for textarea, set value.
    const _planSub = questionInteractions['Plan']?.Structure?.['Plan'];
    if (_planSub) {
      Object.values(_planSub).forEach((field: any) => {
        if (!field?.Input) return;
        field.Input = async (val: string, replyType: string) => {
          if (!val || SKIP_VALUES.has(val.trim().toLowerCase())) return;
          document.querySelectorAll('#root_plan fieldset.is-editing').forEach((el) => {
            if (el.id !== 'root_plan_plan') { const leg = el.querySelector('legend[id]'); if (leg instanceof HTMLElement) leg.click(); }
          });
          const fb = document.querySelector('#root_plan_plan .fields-box'); if (fb instanceof HTMLElement) fb.click();
          await waitForDom(() => document.querySelector('#root_plan_plan_comments')?.tagName === 'TEXTAREA', 1500);
          const ta = document.querySelector('#root_plan_plan_comments');
          if (ta instanceof HTMLTextAreaElement) setReactTextareaValue(ta, replyType === 'append' ? (ta.value ? ta.value + '\n' + val : val) : val);
        };
      });
    }

    /* End Mid Level Section Parsing */

    /* Start Coding Justification Parsing */
    // if on the coding section we fail, don't bring the whole thing down...
    try {
      const codingSection = document.querySelector(
        '#root_charge_summary_title'
      );
      if (codingSection) {
        codingSection.click();
        await new Promise((resolve) => setTimeout(resolve, 10));

        // Click all enabled "add comment" buttons
        const addCommentButtons = document.querySelectorAll(
          'button.ta-add-comment:not([disabled])'
        );
        for (const button of addCommentButtons) {
          button.click();
          await new Promise((resolve) => setTimeout(resolve, 10));
        }

        const codingJustifications = document.querySelectorAll(
          'textarea[id^="root_charge_summary"]'
        );

        // This top level is always Charge Summary
        let topLevelSection = 'Charge Summary';
        if (!questionInteractions[topLevelSection]) {
          questionInteractions[topLevelSection] = {
            Type: 'Flex',
            Key: topLevelSection,
            Question: topLevelSection,
            Structure: {}
          };
        }

        // Mid Level Section Key (Always Charges)
        let midLevelSectionKey = 'Charges';

        let charges = {};
        for (let i = 0; i < codingJustifications.length; i++) {
          const result = await parseCodingJustification(
            codingJustifications[i],
            codingSection
          );
          const key = Object.keys(result)[0];
          charges[key] = result[key];
        }

        // Add this mid Level Section to the Top Level Section as flex
        questionInteractions[topLevelSection].Structure[midLevelSectionKey] =
          charges;
      }
    } catch (error) {
      console.error('Error in Coding Justification Parsing:', error);
      getLogFromInjector();
    }

    /* End Coding Justification Parsing */

    // Throw it into the questions array the injector is looking for
    for (let key in questionInteractions) {
      const interaction = questionInteractions[key];
      const question = {
        Key: key,
        Question: key,
        ExistingNote: interaction?.ExistingNote || '',
        Type: interaction.Type
      };

      if (interaction.Choices) {
        question.Choices = Object.keys(interaction.Choices);
      }

      if (interaction.Type === 'Flex' && interaction.Structure) {
        question.Structure = {};
        if (key === 'Objective' || key === 'Subjective') {
          question.Structure.prune_early = true;
        }

        for (const [fieldKey, fieldVal] of Object.entries(
          interaction.Structure
        )) {
          const sanitizedFieldKey = sanitizeFieldName(fieldKey);

          if (
            sanitizedFieldKey === 'Assessment' &&
            fieldVal &&
            Object.keys(fieldVal).length === 0
          ) {
            continue; // drop Assessment:{}
          }
          // Not fair to assume our above functions worked correctly... we ran into an issue
          // where existing values was bombing... and there was a null here... so we defend
          // above and here too
          if (fieldVal) {
            question.Structure[sanitizedFieldKey] = {};
            for (const [subKey, subVal] of Object.entries(fieldVal)) {
              const sanitizedSubKey = sanitizeFieldName(subKey);
              question.Structure[sanitizedFieldKey][sanitizedSubKey] = {
                ...(subVal?.Choices && { Choices: subVal.Choices }),
                ...(subVal?.Type && { Type: subVal.Type }),
                ...(subVal?.['Existing Value'] && {
                  'Existing Value': subVal['Existing Value']
                }),
                ...(subVal?.Description && { Description: subVal.Description })
              };
            }
          }
        }
      }

      if (
        question.Type !== 'Flex' ||
        Object.keys(question.Structure || {}).length > 0 ||
        // When a top-level section (e.g., Subjective) is marked 'skip', its subsections
        // are never traversed, leaving question.Structure empty. Because of this,
        // the check `Object.keys(question.Structure || {}).length > 0` filtered these
        // questions out before pushing. As a result, they could not reappear later if
        // the user wants to mark the section back to 'unskip'.
        replyType?.TopLevelReplyType?.[question.Key] === 'no_reply'
      ) {
        questions.push(question);
      }
    }
    // Remove duplicate questions while keeping the original order
    questions = questions.filter(
      (question, index, self) =>
        index === self.findIndex((q) => q.Question === question.Question)
    );

    // Scroll back to the original position
    scrollElement.scrollTop = scrollTop;

    // This scrape revealed every section, so let note capture harvest the values
    // while they're in hand. Never allowed to affect the panel's result.
    try {
      seedCaptureCacheFromScrape(questions, captureNoteKeyAtScrapeStart);
    } catch (_) {
      // ignored on purpose
    }

    // Update cache and return
    lastGetQuestionsAt = Date.now();
    // Send dropdownMisses and dropdownCache only when they have content
    const result = { questions: questions };

    if (Object.keys(dropdownMisses).length > 0) {
      result['dropdownMisses'] = dropdownMisses;
      dropdownMisses = {};
    }

    if (Object.keys(dropdownCache).length > 0) {
      result['dropdownCache'] = dropdownCache;
    }
    cachedQuestions = result;
    return result;
  } finally {
    endOverlay();
  }
}

async function handleAssessmentSection() {
  // Fallback for Assessment - Summary if not already included
  let assessmentSpan = document.querySelector(
    '#root_assessment_assessment_comments'
  );
  if (!assessmentSpan) {
    // Attempt to expand the section via its toggle button
    const toggle = document.querySelector(
      '#root_assessment_assessment .edit-toggle'
    );
    if (toggle) {
      toggle.click();
      await sleep(100);
      toggle.click(); // Some sections require two toggles
      await sleep(100);
    }
    // Try again to locate the span
    assessmentSpan = document.querySelector(
      '#root_assessment_assessment_comments'
    );
  }
  const assessmentFieldset = document.querySelector(
    'fieldset#root_assessment_assessment'
  );

  // Updated to use Flex and push into 'Assessment' if it already exists
  if (assessmentSpan && assessmentFieldset) {
    const topLevelSection = 'Assessment';
    const midLevelSectionKey = 'Assessment - Section';
    if (!questionInteractions[topLevelSection]) {
      questionInteractions[topLevelSection] = {
        Type: 'Flex',
        Key: topLevelSection,
        Question: topLevelSection,
        Structure: {}
      };
    }
    if (!questionInteractions[topLevelSection].Structure[midLevelSectionKey]) {
      questionInteractions[topLevelSection].Structure[midLevelSectionKey] = {};
    }
    questionInteractions[topLevelSection].Structure[midLevelSectionKey][
      midLevelSectionKey
    ] = {
      Input: async (val, replyType) => {
        const toggle = assessmentFieldset.querySelector('.edit-toggle');
        if (toggle) {
          toggle.click();
          await sleep(100);
          toggle.click();
          await sleep(100);
        }
        await revealAssessmentTextarea();
        clickCarryForwardIfPresent('root_assessment_assessment');
        const editable =
          document.querySelector(
            'textarea#root_assessment_assessment_comments'
          ) || assessmentFieldset.querySelector('textarea');
        if (!editable) {
          console.warn(
            `Textarea still not found after toggling: Assessment - Section`
          );
          return;
        }
        if (replyType === 'append') {
          return await setReactTextareaValue(
            editable,
            editable.value + '\n' + val
          );
        } else {
          return await setReactTextareaValue(editable, val);
        }
      }
    };
  }
}

async function revealAssessmentTextarea() {
  const fieldset = document.querySelector('#root_assessment_assessment');
  if (!fieldset) {
    console.warn('Assessment fieldset not found');
    return;
  }

  const carryForward = fieldset.querySelector(
    '.edit-carry-forward-preview[role="button"]'
  );
  const toggle = fieldset.querySelector('.edit-toggle');

  if (carryForward) {
    carryForward.click();
    console.log('Clicked carry-forward preview');
  } else if (toggle) {
    toggle.click();
    setTimeout(() => toggle.click(), 150); // Some sections need double click
    console.log('Toggled assessment section');
  } else {
    console.warn('No carry-forward or toggle found for Assessment');
    return;
  }

  const maxAttempts = 10;
  let attempts = 0;
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      const textarea = document.querySelector(
        '#root_assessment_assessment_comments'
      );
      if (textarea && textarea.tagName === 'TEXTAREA') {
        console.log('Textarea now editable:', textarea);
        clearInterval(interval);
        resolve(true);
      } else {
        attempts++;
        if (attempts >= maxAttempts) {
          console.warn('Textarea still not found after interactions');
          clearInterval(interval);
          resolve(false);
        }
      }
    }, 300);
  });
}

function clickCarryForwardIfPresent(fieldsetId) {
  const fieldset = document.getElementById(fieldsetId);
  if (!fieldset) {
    console.warn(`Fieldset not found: ${fieldsetId}`);
    return;
  }

  // Look for the carry-forward button inside this fieldset
  const button = fieldset.querySelector(
    '.carry-forward-action-panel .accept-carry-forward-button'
  );
  if (button) {
    button.click();
    console.log(`Clicked carry-forward button for ${fieldsetId}`);
  } else {
    console.log(`No carry-forward button found for ${fieldsetId}`);
  }
}

async function revealFlowsheetTextarea() {
  const click = async (selectorOrEl, label = '') => {
    const el =
      typeof selectorOrEl === 'string'
        ? document.querySelector(selectorOrEl)
        : selectorOrEl;
    if (el) {
      el.click();
      console.log(`Clicked: ${label || el.tagName}`);
      await sleep(300);
    } else {
      console.warn(`Not found: ${label || selectorOrEl}`);
    }
  };

  await click('#root_flowsheet__title', 'Flowsheet legend');
  await click(
    '#root_flowsheet_activities__title',
    'Activities/Interventions legend'
  );
  await click(
    '#root_flowsheet_activities .edit-toggle',
    'Flowsheet .edit-toggle'
  );

  const carryPreview = document.querySelector(
    '#root_flowsheet_activities .edit-carry-forward-preview'
  );
  if (carryPreview) {
    carryPreview.click();
    await sleep(300);
    carryPreview.click();
    console.log('Clicked middle carry-forward preview box');
  } else {
    console.warn('Carry-forward preview not found');
  }

  // Wait for table to load
  let attempts = 0;
  while (
    document.querySelectorAll(
      'table#root_flowsheet_activities_activity_list tbody tr'
    ).length === 0 &&
    attempts++ < 10
  ) {
    await sleep(400);
  }

  const rowCount = document.querySelectorAll(
    'table#root_flowsheet_activities_activity_list tbody tr'
  ).length;
  if (rowCount > 0) {
    console.log(`Flowsheet table is visible with ${rowCount} row(s)`);
  } else {
    console.warn('Flowsheet table still not found');
  }
}

async function handleFlowsheetSection() {
  try {
    const check = 'Flowsheet';

    if (!questionInteractions[check]) {
      questionInteractions[check] = {
        Type: 'Flex',
        Key: check,
        Question: check,
        Structure: {},
        ReplyType: 'overwrite'
      };
    } else if (Object.keys(questionInteractions[check]?.Structure).length > 1) {
      console.log('Flowsheet already handled');
      return;
    }
    const structure = questionInteractions[check].Structure;

    const rows = document.querySelectorAll(
      'table#root_flowsheet_activities_activity_list tbody tr'
    );

    rows.forEach((row, index) => {
      if (!row) {
        console.warn(`Skipping null/undefined row at index ${index}`);
        return;
      }
      const nameEl = row.querySelector('.activity-list--activity-name div');
      if (!nameEl) {
        console.warn(`Skipping row ${index + 1}: No activity name element`);
        return;
      }
      const activityName =
        nameEl?.textContent?.trim() || `Activity ${index + 1}`;

      if (!activityName) {
        console.warn(`Skipping row ${index + 1}: No activity name`);
        return;
      }

      let sanitizedActivityName = sanitizeFieldName(activityName);
      let finalName = sanitizedActivityName;
      let counter = 2;
      while (structure[finalName]) {
        finalName = `${sanitizedActivityName} (${counter++})`;
      }

      const container = {};
      const inputs = row.querySelectorAll('input, textarea');
      if (!inputs || inputs.length === 0) {
        console.warn(
          `Row ${index + 1} ("${finalName}") has no inputs, skipping.`
        );
        return;
      }
      inputs.forEach((input) => {
        const parentCell = input.closest('td');
        if (!parentCell) return;

        // Dynamic label extraction from class
        const classAttr = parentCell.getAttribute('class') || '';
        const match = classAttr.match(/activity-list--([a-zA-Z-]+)/);
        if (!match) return;
        let label = match[1].replace(/-/g, ' ').trim();
        label = label.charAt(0).toUpperCase() + label.slice(1);
        // Sanitize label to ensure it's safe for JSON schema property names
        label = sanitizeFieldName(label);
        if (input.type === 'checkbox') {
          container[label] = {
            Choices: ['Yes', 'No'],
            Input: (val) => {
              if (val === 'Yes' && !input.checked) {
                input.click();
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          };
        } else {
          const isNumeric = input.type === 'number';
          container[label] = {
            ...(isNumeric && { Type: 'Integer' }),
            ...(input.value && { 'Existing Value': input.value }),
            Input: async (val) => {
              await fillFlowsheetInput(input.id, val);
            }
          };
        }
      });

      if (Object.keys(container).length > 0) {
        structure[finalName] = container;
      }
    });

    // Handle Additional Comments textarea
    const additionalCommentsTextarea = document.getElementById(
      'root_flowsheet_activities_additional_comments'
    );
    if (additionalCommentsTextarea) {
      if (!questionInteractions[check].Structure['Additional Comments']) {
        questionInteractions[check].Structure['Additional Comments'] = {};
      }
      questionInteractions[check].Structure['Additional Comments'][
        'Additional Comments'
      ] = {
        Description:
          'Only include notable observations not already documented elsewhere. Avoid repeating information from other sections.',
        Input: async (val) => {
          await fillFlowsheetInput(additionalCommentsTextarea.id, val);
        }
      };
    }
  } catch (error) {
    console.error('Error in handleFlowsheetSection:', error);
    await getLogFromInjector();
  }
}

async function fillFlowsheetInput(inputId, val) {
  if (!val) {
    console.log(`Skipping empty value`);
    return;
  }

  const fieldset = document.getElementById('root_flowsheet_activities');
  if (fieldset) {
    const toggle = fieldset.querySelector('.edit-toggle');
    if (toggle) {
      toggle.click();
      await sleep(100);
      toggle.click();
      await sleep(150);
    }
  }
  const input = document.getElementById(inputId);
  if (!input) {
    console.warn(`Still can't find input: ${inputId}`);
    return;
  }

  const proto =
    input.tagName === 'TEXTAREA'
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  setter?.call(input, val);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

// Generic waiter for elements within an optional container
async function waitForFieldSetHasData(fieldSetSelector, options = {}) {
  const { timeout = 4000, interval = 100 } = options || {};
  const start = Date.now();
  while (Date.now() - start < timeout) {
    let fieldsetElement = document.querySelector(fieldSetSelector);
    if (fieldsetElement.querySelector('.form-group')) return fieldsetElement;
    await sleep(interval);
  }
  return null;
}

// There's a change we get 0 sections... that's bad. I bet it's a carry forward case...
async function openCarryForward(elementSelector) {
  // await sleep(400); // Will work but not clever... and maybe unreliable?
  let fieldset = document.querySelector(elementSelector)?.closest('fieldset');
  let fieldSetSelector = getSelector(fieldset);
  await waitForFieldSetHasData(fieldSetSelector);
  await sleep(50);
  let carryForwardButton = document
    .querySelector(
      'button.carry-forward-action-panel, .carry-forward-action-panel button'
    )
    ?.click();
  await document.querySelector(elementSelector)?.click();
}

function findFieldLabel(element) {
  if (!element) return 'Unknown';

  // Combine the closest fieldset ID and the element's own ID for full path context
  const fieldsetId = element.closest('fieldset')?.id || '';
  const elementId = element.id || '';
  const combinedId = [fieldsetId, elementId].filter(Boolean).join('_');

  // Parse and normalize ID parts
  const idParts = combinedId
    .split('root_')
    .pop()
    .split('_')
    .filter(
      (p) =>
        ![
          'objective',
          'root',
          'value',
          'field',
          'form',
          'edit',
          'box',
          'string',
          'array',
          'object',
          'title',
          'schema',
          'depth',
          'sc',
          'id',
          'data',
          'group',
          'fieldset',
          'true',
          'false'
        ].includes(p.toLowerCase())
    )
    .map((p) => p.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));

  // Deduplicate consecutive repeated words
  const cleaned = idParts.filter(
    (p, i) => i === 0 || p.toLowerCase() !== idParts[i - 1].toLowerCase()
  );

  // Build label
  const label = cleaned.join(' - ');
  return sanitizeFieldName(label || 'Unknown');
}

function detectElementType(element) {
  if (
    element.matches(
      'textarea.form-control, input[type="text"], input[type="number"]'
    )
  ) {
    // Exclude .pt-input and .pt-input-ghost
    if (
      !element.classList.contains('pt-input') &&
      !element.classList.contains('pt-input-ghost')
    ) {
      return 'text';
    } else {
      return 'dropdown'; // sometimes .pt-input are dropdowns
    }
  } else if (element.matches('input[type="radio"]')) {
    return 'radio';
  } else {
    return 'dropdown';
  }
}

async function handleSubQuestion(
  subQuestion,
  key,
  qSelector,
  sectionName,
  dropdownCache,
  processedRadioGroups,
  localQuestionInteractions,
  noteType
) {
  if (subQuestion.type === 'text') {
    if (sectionName === 'Assessment') {
      await handleAssessmentSection();
      return;
    }
    if (sectionName === 'Activities/Interventions') {
      await handleFlowsheetSection();
    }

    const isAdditionalComments =
      key?.toLowerCase?.()?.includes('additional') &&
      key?.toLowerCase?.()?.includes('comments');
    localQuestionInteractions[key] = {
      'Existing Value': getExistingNote(subQuestion.element.value),
      ...(isAdditionalComments && {
        Description:
          'Only include notable observations not already documented elsewhere. Avoid repeating information from other sections.'
      }),
      Input: (val, replyType) => {
        let localQuestionSelector = qSelector;
        let localSubSectionSelector = getSelector(subQuestion.element);
        return answerShortAnswer(
          localQuestionSelector,
          localSubSectionSelector,
          val,
          replyType
        );
      }
    };
  } else if (subQuestion.type === 'dropdown') {
    let choices = await extractDropdownChoices(
      subQuestion.element,
      dropdownCache,
      noteType
    );
    const isMultiSelect =
      subQuestion.element.classList?.contains('pt-input-ghost');
    const capturedQSelector = qSelector;
    const capturedSubSelector = getSelector(subQuestion.element);
    localQuestionInteractions[key] = {
      ...(isMultiSelect
        ? {
            isMultiSelect: true,
            Description: `Select all applicable answers. If none apply, reply with 'None'. Provide your response as a list of choices separated by a '$' symbol. The available options are: ${choices.join(', ')}`
          }
        : { Choices: choices }),
      Input: async (val, replyType) => {
        if (val == 'null' || !val || val == '') return;
        if (isMultiSelect) {
          // Expand the section once only if the tag-input container isn't already
          // visible — clicking an already-expanded section collapses it.
          const ghostEl = document.querySelector(capturedSubSelector);
          const tagInput = ghostEl?.closest('.pt-tag-input');
          const alreadyVisible =
            tagInput && tagInput.getBoundingClientRect().height > 0;
          if (!alreadyVisible) {
            const sectionEl = document.querySelector(capturedQSelector);
            if (sectionEl) {
              await sleep(250);
              sectionEl.click();
              await sleep(10);
            }
          }
          const values = val
            .split('$')
            .map((v) => v.trim())
            .filter(Boolean);
          for (const v of values) {
            await answerDropdown(
              capturedQSelector,
              capturedSubSelector,
              v,
              replyType,
              true
            );
          }
        } else {
          return await answerDropdown(
            capturedQSelector,
            capturedSubSelector,
            val,
            replyType
          );
        }
      }
    };
  } else if (subQuestion.type === 'radio') {
    const group = subQuestion.element.closest('.field-radio-group');
    if (!group) {
      console.warn('Radio group not found for element');
    } else {
      if (processedRadioGroups.has(group)) {
        // Already handled this group
        return;
      }
      processedRadioGroups.add(group);

      const choices = await extractRadioChoices(group);
      const checked = group.querySelector('input[type="radio"]:checked');
      const alreadyChecked = checked ? true : false;
      localQuestionInteractions[key] = {
        Choices: choices,
        Input: async (val) => {
          if (val == 'null' || !val || val == '') return;
          if (alreadyChecked) return;
          return await answerRadio(
            qSelector,
            getSelector(subQuestion.element),
            val
          );
        }
      };
    }
  }
}

function shouldSkipByReplyType(replyType, textToCheck) {
  let subSectionsReplyType = replyType.SubSectionsReplyType || {};
  if (Object.keys(subSectionsReplyType).length === 0 || !textToCheck) {
    return false;
  }
  return Object.keys(subSectionsReplyType).some((key) => {
    const value = subSectionsReplyType[key];
    const isNoReply = value === 'no_reply';
    // Check both directions: key contains textToCheck OR textToCheck contains key
    const matches = textToCheck === key;
    return matches && isNoReply;
  });
}

async function parseFieldBox(q, dropdownCache, replyType) {
  try {
    // Store the selector right away (In case it does change after we click)
    const qSelector = getSelector(q);

    const fieldsetElement = q.closest('fieldset');

    if (!fieldsetElement) {
      console.error('Fieldset element not found');
      return;
    }

    const sectionHeadElement =
      fieldsetElement.parentElement.querySelector('legend') ||
      fieldsetElement.querySelector('legend');

    if (!sectionHeadElement) {
      console.error('Legend element not found');
      return;
    }

    const sectionName = sanitizeLegendText(sectionHeadElement);

    // Check if this section should be skipped BEFORE expanding
    if (shouldSkipByReplyType(replyType, sectionName)) {
      console.log('Skipping Section (no_reply): ', sectionName);

      // Return in the SAME shape as normal localQuestionInteractions
      return {
        [sectionName]: {
          'Existing Value': null,
          Input: async () => {
            return;
          }
        }
      };
    }
    q.click();
    q.click();

    await new Promise((resolve) => setTimeout(resolve, 10));
    // Collapsed edit-groups render read-only <p>s with the input ids; wait for the real inputs.
    if (fieldsetElement.classList.contains('edit-group')) {
      const id = fieldsetElement.id;
      await waitForDom(() => !!document.getElementById(id)?.classList.contains('is-editing'), 1500);
    }

    // Manually reveal special sections before reading their headers
    if (fieldsetElement.id === 'root_assessment_assessment') {
      await revealAssessmentTextarea();
    }
    if (fieldsetElement.id === 'root_flowsheet_activities') {
      await revealFlowsheetTextarea();
    }

    let subQuestions = getFilteredFormElements();
    console.log(subQuestions);
    let SubQuestionsWithType = [];
    const processedRadioGroups = new Set();

    if (subQuestions.length === 0) {
      console.warn('No sub questions found, trying carry forward');
      await openCarryForward(qSelector);
      subQuestions = getFilteredFormElements();
    }

    subQuestions.forEach((el) => {
      const type = detectElementType(el);
      SubQuestionsWithType.push({ element: el, type });
    });

    // At this point we are in the Mid Level Section and seeing each of the bottom Level Sections -> Subjective - Patient Presentation - Primary Concern
    let localQuestionInteractions = {};
    for (let j = 0; j < SubQuestionsWithType.length; j++) {
      const subQuestion = SubQuestionsWithType[j];
      let questionText = findFieldLabel(subQuestion.element);
      if (
        questionText == '' ||
        !questionText ||
        questionText.includes('Unknown') ||
        questionText == null ||
        questionText == undefined ||
        questionText.startsWith('Therapy - Visit') ||
        questionText.includes('Date - Of - Injury - Onset') ||
        questionText.includes(' Medical - History - Allergies') ||
        questionText.endsWith('Current - Conditions') ||
        questionText.endsWith('Previous - Conditions 2')
      ) {
        continue;
      }
      let pageInfo = getPageInfo();
      const noteType = pageInfo['Note Type'];

      // Common check for all note types
      if (!questionText) {
        console.error('Question text not found');
        continue;
      }

      // Check conditions based on the note type
      const isInvalidInitialEval =
        noteType === 'Initial Evaluation' && questionText === 'Section';
      const isInvalidProgressNote =
        noteType === 'Progress Note' &&
        questionText === 'Section' &&
        sectionName !== 'Assessment';

      if (isInvalidInitialEval || isInvalidProgressNote) {
        console.error(`Invalid or missing question text for ${noteType}`);
        continue;
      }

      if (sectionName === 'Unknown Question') {
        console.error('Section name not found');
        continue;
      }
      console.log('Section Name: ' + sectionName);

      let keyBase = sanitizeFieldName(questionText);
      let key = keyBase;
      let counter = 2;
      while (localQuestionInteractions[key]) {
        key = `${keyBase} (${counter++})`;
      }

      await handleSubQuestion(
        subQuestion,
        key,
        qSelector,
        sectionName,
        dropdownCache,
        processedRadioGroups,
        localQuestionInteractions,
        noteType
      );
      // console.log('Question: ' + sectionName + ' - ' + questionText);
    }

    // Remove any 'Additional - Comments' key based on section type
    const isAdditionalComments = (k: string) =>
      /Additional\s*-\s*Comments$/i.test(k);
    const nonAdditionalKeys = Object.keys(localQuestionInteractions).filter(
      (k) => !isAdditionalComments(k)
    );
    const topLevel = getTopLevelSection(q);
    const shouldRemoveAdditionalComments =
      topLevel === 'Objective' && nonAdditionalKeys.length > 1;
    if (shouldRemoveAdditionalComments) {
      Object.keys(localQuestionInteractions)
        .filter(isAdditionalComments)
        .forEach((k) => delete localQuestionInteractions[k]);
    }

    return localQuestionInteractions;
  } catch (error) {
    console.error('Error: ' + error);
    getLogFromInjector();
  }
}

/**
 * Opens a BlueprintJS dropdown by simulating user interaction
 *
 * BlueprintJS Dropdown Handling:
 * - BlueprintJS uses React components that render dropdown overlays in portals
 * - The actual dropdown input (.pt-input) triggers overlay rendering
 * - Multi-select dropdowns use .pt-input-ghost (hidden input) + .pt-tag-input (visible container)
 * - Single-select dropdowns use .pt-input directly
 * - Overlays appear as .pt-overlay.pt-overlay-open with .pt-menu-item children
 *
 * This function handles both single-select and multi-select dropdowns by:
 * 1. Detecting multi-select ghost inputs and targeting their visible container
 * 2. Simulating focus events to ensure React state updates
 * 3. Dispatching click events to trigger BlueprintJS overlay rendering
 *
 * @param {HTMLElement} targetElement - The dropdown element to open
 */
function closeOpenDropdowns() {
  if (!document.querySelector('.pt-portal .pt-overlay.pt-overlay-open')) return;
  // blur() and focus() fire trusted browser events (isTrusted: true), unlike new MouseEvent().
  // Blurring the active element and tag inputs signals focus left the dropdown trigger.
  const active = document.activeElement as HTMLElement | null;
  if (active && active !== document.body) {
    active.blur();
  }
  document
    .querySelectorAll<HTMLElement>('.pt-tag-input, .pt-input-ghost')
    .forEach((el) => el.blur());
}

async function openDropdown(targetElement) {
  if (!targetElement) {
    console.warn('No element for openDropdown');
    return;
  }

  // BlueprintJS Multi-select Handling:
  // Multi-selects use .pt-input-ghost (hidden) + .pt-tag-input (visible container)
  // We need to click the visible container, not the hidden ghost input
  if (targetElement.classList?.contains('pt-input-ghost')) {
    const wrapper = targetElement.closest('.pt-tag-input');
    if (wrapper) {
      targetElement = wrapper; // Target the visible container instead
    }
  }

  // Force layout calculation to ensure element is ready for interaction
  targetElement.getBoundingClientRect();

  // BlueprintJS Focus Events:
  // React components often rely on focus events to update internal state
  // These events ensure the dropdown component knows it should be "active"
  targetElement.focus();
  targetElement.dispatchEvent(new FocusEvent('focus', { bubbles: true }));
  targetElement.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));

  // BlueprintJS Click Simulation:
  // BlueprintJS dropdowns listen for click events to open overlays
  // The overlay is rendered in a portal (.pt-overlay.pt-overlay-open)
  // This triggers the React component to show the dropdown options
  const opts = {
    bubbles: true,
    cancelable: true
  };
  ['click'].forEach((t) =>
    targetElement.dispatchEvent(new MouseEvent(t, opts))
  );
}

function normalizeDropdownKey(idOrKey = '') {
  return (
    idOrKey
      .toLowerCase()
      // remove "root_" or other prefixes
      .replace(/^root_/, '')
      // remove all underscores followed by digits (e.g., _0, _1, _t3_t6)
      .replace(/_[0-9]+/g, '')
      // remove any spinal or numeric ranges like _t3_t6, _c5_c7, etc.
      .replace(/_(t|c|l|s)\d+(_(t|c|l|s)\d+)?/g, '')
      // remove side indicators appearing anywhere
      .replace(/_?(left|right)_?/g, '_')
      .trim()
  );
}

/**
 * Extracts dropdown choices from BlueprintJS dropdowns with intelligent caching
 *
 * BlueprintJS Dropdown Extraction Process:
 * 1. SECTION IDENTIFICATION: Maps dropdown IDs to standardized section keys
 *    - Joint mobility, ROM, strength tests, etc. get normalized keys
 *    - This enables cache sharing across similar dropdowns
 * 2. CACHE CHECK: Uses local storage to persist dropdown choices (never expires)
 *    - Prevents re-scraping the same dropdowns repeatedly
 *    - Cache key is normalized to handle dynamic IDs (e.g., _0, _1 suffixes)
 * 3. OVERLAY DETECTION: Finds BlueprintJS dropdown overlays
 * 4. CHOICE EXTRACTION: Scrapes menu items from overlays
 * 5. MISS TRACKING: Records "misses" for monitoring
 *    - When a dropdown isn't in cache, it's a "miss"
 *    - Misses are sent to Slack for analysis and cache improvement
 */
async function extractDropdownChoices(
  dropdownElement,
  dropdownCache,
  noteType
) {
  try {
    //dropdownCache {initialEvaluation: {joint_mobility: ['Joint Mobility 1', 'Joint Mobility 2'], rom: ['ROM 1', 'ROM 2'], strength_gross: ['Strength Gross 1', 'Strength Gross 2'], strength_manual: ['Strength Manual 1', 'Strength Manual 2'], vertebral_mobility: ['Vertebral Mobility 1', 'Vertebral Mobility 2']}, progressNote: {joint_mobility: ['Joint Mobility 1', 'Joint Mobility 2'], rom: ['ROM 1', 'ROM 2'], strength_gross: ['Strength Gross 1', 'Strength Gross 2'], strength_manual: ['Strength Manual 1', 'Strength Manual 2'], vertebral_mobility: ['Vertebral Mobility 1', 'Vertebral Mobility 2']}, dailyNote: {joint_mobility: ['Joint Mobility 1', 'Joint Mobility 2'], rom: ['ROM 1', 'ROM 2'], strength_gross: ['Strength Gross 1', 'Strength Gross 2'], strength_manual: ['Strength Manual 1', 'Strength Manual 2'], vertebral_mobility: ['Vertebral Mobility 1', 'Vertebral Mobility 2']}, discharge: {joint_mobility: ['Joint Mobility 1', 'Joint Mobility 2'], rom: ['ROM 1', 'ROM 2'], strength_gross: ['Strength Gross 1', 'Strength Gross 2'], strength_manual: ['Strength Manual 1', 'Strength Manual 2'], vertebral_mobility: ['Vertebral Mobility 1', 'Vertebral Mobility 2']}}
    let choices = [];

    // Handle string selectors
    if (typeof dropdownElement === 'string')
      dropdownElement = document.querySelector(dropdownElement);
    if (!dropdownElement) {
      console.warn('No dropdown element found');
      return choices;
    }

    // STEP 1: SECTION IDENTIFICATION
    // Map dropdown IDs to cache keys.
    // Single-select: normalize by section type (joint_mobility, rom, etc.)
    // Ghost inputs (multi-select): cache by type suffix (_description, _end_feel, etc.)
    //   All description fields share the same options; same for end_feel, etc. — so
    //   we only need to open one dropdown per suffix type instead of one per field.
    const isGhostInput = dropdownElement.classList?.contains('pt-input-ghost');

    const id = dropdownElement.id || '';
    let baseKey = '';

    if (isGhostInput) {
      // Extract the type suffix: last meaningful segment before "_value"
      // e.g. "...flexion_description_value" → "ghost_description"
      //      "...flexion_end_feel_value"    → "ghost_end_feel"
      const suffixMatch = id
        .replace(/_value$/, '')
        .match(/_(description|end_feel|quality|pain_type|special_test\w*)$/);
      baseKey = suffixMatch ? `ghost_${suffixMatch[1]}` : normalizeDropdownKey(id);
    } else {
      if (/_joint_mobility_|passive_joint_mobility|jointmobility/.test(id)) {
        baseKey = 'joint_mobility';
      } else if (/_rom_|range_of_motion/.test(id)) {
        baseKey = 'rom';
      } else if (/strength_gross_muscle_tests/.test(id)) {
        baseKey = 'strength_gross';
      } else if (/strength_manual_muscle_tests/.test(id)) {
        baseKey = 'strength_manual';
      } else if (/vertebral_mobility/.test(id)) {
        baseKey = 'vertebral_mobility';
      } else {
        baseKey = id;
      }
      baseKey = normalizeDropdownKey(baseKey);
    }

    if (!baseKey) {
      console.error(`No base key found for "${id}"`);
      return choices;
    }

    // STEP 2: CACHE CHECK
    // Both single-select and ghost inputs use cache — ghost inputs keyed by type
    // suffix so all description fields share one scrape, all end_feel fields share one.
    if (dropdownCache[noteType][baseKey]) {
      return [...dropdownCache[noteType][baseKey]];
    }

    // STEP 3: OPEN DROPDOWN AND FIND OVERLAY
    // Trigger BlueprintJS dropdown to open and render overlay.
    // Snapshot existing overlays first so we only match the newly-opened one —
    // stale portals from previous dropdowns may still be in the DOM.
    const itemSelector =
      ".pt-menu-item, [role='option'], li, option, .pt-text-overflow-ellipsis.pt-fill, .multiselect-menu-item";
    const overlaySelector =
      ".pt-overlay.pt-overlay-open, [role='listbox'], .bp4-portal, .pt-multi-select-popover, .multi-select-widget-popover";

    const existingOverlays = new Set(
      Array.from(document.querySelectorAll(overlaySelector))
    );
    await openDropdown(dropdownElement);

    let overlay: Element | null = null;
    await waitForDom(() => {
      const allOverlays = Array.from(
        document.querySelectorAll(overlaySelector)
      );
      const newOverlays = allOverlays.filter(
        (el) => !existingOverlays.has(el) && el.querySelector(itemSelector)
      );
      if (newOverlays.length > 0) {
        overlay = newOverlays[newOverlays.length - 1];
        return true;
      }
      return false;
    });

    console.log(
      overlay
        ? `Found overlay for "${baseKey}"`
        : `No overlay found for "${baseKey}"`
    );
    if (!overlay) {
      console.error(`No overlay with data found for "${baseKey}"`);
      return [];
    }

    // STEP 4: EXTRACT DROPDOWN OPTIONS
    // BlueprintJS uses various selectors for menu items depending on component type
    const menuSelectors = [
      '.pt-menu-item', // Standard BlueprintJS menu items
      "[role='option']", // ARIA-compliant options
      'li', // List items (fallback)
      'option', // Native select options (fallback)
      '.pt-text-overflow-ellipsis.pt-fill', // Text-only menu items
      '.multiselect-menu-item' // Multi-select specific items
    ];

    let foundChoices = [];

    // Extract text content from all menu items
    let nodesArray = [];
    for (const sel of menuSelectors) {
      const nodes = Array.from(overlay.querySelectorAll(sel));
      if (nodes.length > 0) {
        console.log(`Selector "${sel}" found ${nodes.length} nodes`);
      }
      nodesArray.push(...nodes);

      // Clean and extract text content
      const uniqueNodes = Array.from(new Set(nodesArray));
      const nodeChoices = uniqueNodes
        .map((node) => {
          // Clone node to avoid modifying original DOM
          const clone = node.cloneNode(true);
          // Remove SVG icons and titles that might interfere with text extraction
          clone.querySelectorAll('svg, title').forEach((el) => el.remove());
          return clone.textContent?.trim();
        })
        .filter((txt) => !!txt); // Remove empty strings

      foundChoices.push(...nodeChoices);
    }

    // STEP 5: DEDUPLICATION AND VALIDATION
    choices = Array.from(new Set(foundChoices.map((x) => x.trim())));

    // Detect cache conflicts (when cached data differs from new data)
    if (dropdownCache[noteType][baseKey]) {
      const prev = dropdownCache[noteType][baseKey];
      const overlap = choices.filter((c) => prev.includes(c));
      if (overlap.length > 0) {
        console.warn(
          `Cache merge detected for "${baseKey}" → overlapping choices:`,
          overlap
        );
      }
    }

    // STEP 6: CACHE STORAGE AND MISS TRACKING
    if (choices.length > 0) {
      const prev = dropdownCache[noteType][baseKey];
      // MISS TRACKING: Record when we encounter a new dropdown (cache miss)
      // This data is sent to Slack for monitoring and cache improvement
      if (!prev) {
        dropdownMisses[baseKey] = [...choices];
      }
      // Store choices in persistent cache
      dropdownCache[noteType][baseKey] = [...choices];

      // Warn if cache is being overwritten with different data
      if (prev && prev.length !== choices.length) {
        console.warn(
          `Overwriting cache for "${noteType}" "${baseKey}" (old=${prev.length}, new=${choices.length})`
        );
      }
    } else {
      console.warn(`No valid choices found for "${noteType}" "${baseKey}"`);
    }

    closeOpenDropdowns();
    return choices;
  } catch (err) {
    console.error('Error extracting dropdown choices:', err);
    return [];
  }
}

async function extractRadioChoices(radioInput) {
  try {
    const group = radioInput.closest('.field-radio-group');
    if (!group) return [];
    const spans = Array.from(group.querySelectorAll('label span'));
    const choices = spans.map((s) => s.textContent?.trim()).filter(Boolean);
    return Array.from(new Set(choices));
  } catch (err) {
    console.error('Error extracting radio choices:', err);
    return [];
  }
}

/**
 * Answers a BlueprintJS dropdown by selecting the specified value
 *
 * BlueprintJS Dropdown Answering Process:
 * 1. SECTION EXPANSION: Ensures the dropdown section is expanded/visible
 *    - Clicks the section header to expand collapsed sections
 *    - Handles WebPT's collapsible fieldset structure
 *
 * 2. DROPDOWN OPENING: Triggers BlueprintJS dropdown to open
 *    - Uses openDropdown() to simulate user interaction
 *    - Waits for React to render the overlay portal
 *
 * 3. OVERLAY DETECTION: Finds the correct BlueprintJS overlay
 *    - .pt-overlay.pt-overlay-open: Standard BlueprintJS overlay
 *    - [role='listbox']: ARIA-compliant dropdowns
 *    - .bp4-portal: BlueprintJS v4 portal containers
 *
 * 4. OPTION MATCHING: Locates the target option by text content
 *    - Case-insensitive text matching
 *    - Handles both single-select and multi-select options
 *    - Supports various BlueprintJS menu item structures
 *
 * 5. SELECTION HANDLING: Different approaches for different dropdown types
 *    - SINGLE-SELECT: Clicks the menu item directly
 *    - MULTI-SELECT: Toggles the checkbox within the menu item
 *    - INPUT SYNCHRONIZATION: Updates the underlying input value
 *
 * 6. REACT INTEGRATION: Ensures React state updates properly
 *    - Dispatches input/change events to trigger React handlers
 *    - Uses property descriptor to bypass React's synthetic events
 *    - Scrolls options into view for better UX
 *
 * @param {string} localQuestionSelector - Selector for the question section
 * @param {string} localSubSectionSelector - Selector for the dropdown element
 * @param {string} val - The value to select in the dropdown
 * @param {string} replyType - The type of reply (for logging purposes)
 */
async function answerDropdown(
  localQuestionSelector,
  localSubSectionSelector,
  val,
  replyType,
  skipExpansion = false
) {
  try {
    // STEP 1: SECTION EXPANSION
    // Only expand if the target dropdown isn't already accessible — clicking an
    // already-expanded section collapses it (toggle behaviour in WebPT).
    if (!skipExpansion) {
      const localSubQuestion = document.querySelector(localQuestionSelector);
      if (localSubQuestion) {
        await sleep(250);
        localSubQuestion.click();
        await sleep(10);
      }
    }

    // STEP 2: LOCATE DROPDOWN ELEMENT
    // Find the actual BlueprintJS dropdown input element
    const dropdownEl = document.querySelector(localSubSectionSelector);
    if (!dropdownEl) {
      console.warn(
        `Dropdown not found for selector: ${localSubSectionSelector}`
      );
      return;
    }

    // STEP 3: OPEN DROPDOWN OVERLAY
    // Wait for any stale overlay from the previous call to close before opening a new one.
    // closeOpenDropdowns() only fires blur — the DOM update is async, so without this wait
    // the next call's overlay detection finds the still-closing overlay and searches wrong items.
    await waitForDom(
      () => !document.querySelector('.pt-overlay.pt-overlay-open'),
      500
    );
    await openDropdown(dropdownEl);

    // STEP 4: DETECT BLUEPRINTJS OVERLAYS
    // BlueprintJS renders dropdowns in portals outside the main DOM tree.
    // The overlay appears asynchronously after the click event, so we poll
    // until it appears rather than querying once immediately.
    let overlays: Element[] = [];
    await waitForDom(() => {
      overlays = Array.from(
        document.querySelectorAll(
          ".pt-overlay.pt-overlay-open, [role='listbox'], .bp4-portal"
        )
      );
      return overlays.length > 0;
    });

    if (overlays.length === 0) {
      console.error('No dropdown overlay found for', dropdownEl.id);
      return;
    }

    let choices = [];
    let matchNode = null;

    // STEP 5: SEARCH FOR TARGET OPTION
    // Iterate through overlays to find the one with our target option
    for (const overlay of overlays) {
      // BlueprintJS populates menu items asynchronously after the overlay appears,
      // so poll until nodes are present before proceeding.
      let nodes: NodeListOf<Element> = overlay.querySelectorAll(
        ".pt-menu-item, [role='option'], li, option, .pt-text-overflow-ellipsis.pt-fill, .multiselect-menu-item"
      );
      await waitForDom(() => {
        nodes = overlay.querySelectorAll(
          ".pt-menu-item, [role='option'], li, option, .pt-text-overflow-ellipsis.pt-fill, .multiselect-menu-item"
        );
        return nodes.length > 0;
      });

      if (nodes.length === 0) continue;

      // Convert to array and store
      choices = Array.from(nodes);

      // Find the option that matches our target value (case-insensitive)
      matchNode = choices.find((n) => {
        const text = n.textContent?.trim();
        return text && text.toLowerCase() === val.toLowerCase();
      });

      // STEP 6: HANDLE MULTI-SELECT DROPDOWNS
      // Multi-select dropdowns use checkboxes instead of direct selection.
      // Return immediately after clicking — do NOT fall through to the single-select
      // React integration below, which fires an 'input' event on the ghost input and
      // can reopen the dropdown, causing a second click that deselects the item.
      if (matchNode) {
        const isMultiSelect =
          dropdownEl.classList?.contains('pt-input-ghost') ||
          !!overlay.querySelector(
            '.pt-multi-select-popover, .multi-select-widget-popover'
          );
        if (isMultiSelect) {
          // Ghost inputs render items as <li><a class="pt-menu-item">…</a></li>.
          // matchNode may be the <li> (no pt-menu-item class), so resolve to the
          // child <a> which is the actual clickable element.
          const menuItem = (matchNode.querySelector('a.pt-menu-item') ??
            matchNode.closest('a.pt-menu-item') ??
            matchNode) as HTMLElement;
          menuItem.click();
          console.log(`☑️ Clicked multi-select item for "${val}"`);
          closeOpenDropdowns();
          return;
        }
      }
    }

    // STEP 7: SELECT THE OPTION (single-select only)
    // Clicking the .pt-menu-item directly triggers BlueprintJS's onItemSelect handler
    // and naturally closes the overlay — no need to set the native input value or
    // dispatch synthetic events, which can interfere with React's re-render cycle.
    if (matchNode) {
      matchNode.scrollIntoView({ block: 'center' });
      const clickTarget = (matchNode.querySelector('a.pt-menu-item') ??
        matchNode.closest('a.pt-menu-item') ??
        matchNode) as HTMLElement;
      clickTarget.click();
    } else {
      console.warn(`Value not found in dropdown: "${val}"`);
    }
    closeOpenDropdowns();
  } catch (error) {
    console.error('Error in answerDropdown:', error);
    getLogFromInjector?.();
  }
}

async function answerRadio(
  localQuestionSelector,
  localSubSectionSelector,
  val
) {
  try {
    // Step 1: Locate the clickable header or field label (to expand the section if collapsed)
    const localSubQuestion = document.querySelector(localQuestionSelector);
    if (localSubQuestion) {
      await sleep(250);
      localSubQuestion.click();
      await sleep(10);
    }

    // Handle case where localSubSectionSelector is a string
    let subEl =
      typeof localSubSectionSelector === 'string'
        ? document.querySelector(localSubSectionSelector)
        : localSubSectionSelector;

    if (!subEl) {
      console.warn(
        'Radio input element not found for selector',
        localSubSectionSelector
      );
      return;
    }

    if (val == null || val === '') {
      console.warn('Radio value is null/empty, skipping');
      return;
    }
    const desired = String(val).trim().toLowerCase();
    // Find the radio group container (support BlueprintJS and plain radios)
    const group = subEl.closest(
      '.field-radio-group, .pt-radio-group, .pt-control-group'
    );
    if (!group) {
      console.warn('No radio group found for', localSubSectionSelector);
      return;
    }

    // Support both BlueprintJS and plain/class-generated radios
    const labels = Array.from(
      group.querySelectorAll(
        'label, label.pt-control, label.pt-radio, .pt-radio-group label, label.radio-inline, label.sc-fyjhYU'
      )
    );

    if (labels.length === 0) {
      console.warn(
        'No radio labels found, falling back to input-based matching'
      );
      const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
      for (const r of radios) {
        if (r.value?.trim().toLowerCase() === desired) {
          r.click();
          r.dispatchEvent(new Event('input', { bubbles: true }));
          r.dispatchEvent(new Event('change', { bubbles: true }));
          console.log(`✅ Selected radio option via fallback: "${val}"`);
          return;
        }
      }
      return;
    }

    let targetLabel = null;
    for (const lbl of labels) {
      // Prefer matching within any <span> (common for Blueprint and custom radios)
      const span = lbl.querySelector('span') || lbl;
      const text = (span.textContent || '').trim().toLowerCase();
      if (text === desired || text.includes(desired)) {
        targetLabel = lbl;
        break;
      }
    }

    if (!targetLabel) {
      console.warn(`No matching radio label found for value "${val}"`);
      return;
    }

    const input = targetLabel.querySelector('input[type="radio"]');
    if (!input) {
      console.warn('No input found inside target label for', targetLabel);
      return;
    }

    if (!input.checked) {
      targetLabel.click(); // triggers React or plain handler
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  } catch (err) {
    console.error('Error answering radio input:', err);
  }
}

async function parseCodingJustification(codingJustification, sectionParent) {
  try {
    const codeStr = lookUpForSiblingElement(
      codingJustification,
      '.pt-button-text'
    ).textContent;

    // let xpathExpression = `//*[contains(text(), '${codeStr}')]`;
    // let sectionParent = document.evaluate(xpathExpression, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
    // sectionParent = lookUpForParentElement(sectionParent, 'fieldset');
    const qSelector = getSelector(sectionParent);

    const sanitizedCodeStr = sanitizeFieldName(codeStr);

    let localQuestionInteractions = {};
    localQuestionInteractions[sanitizedCodeStr] = {
      'Existing Value': getExistingNote(codingJustification.value),
      Input: (val, replyType) => {
        let localQuestionSelector = qSelector;
        let localSubSectionSelector = getSelector(codingJustification);
        return answerShortAnswer(
          localQuestionSelector,
          localSubSectionSelector,
          val,
          replyType
        );
      }
    };
    return localQuestionInteractions;
  } catch (error) {
    console.error('Error: ' + error);
    getLogFromInjector();
  }
}

function lookUpForParentElement(element, selector, depth = 10) {
  if (depth === 0) {
    return null;
  }

  if (element.matches(selector)) {
    return element;
  } else {
    return lookUpForParentElement(element.parentElement, selector, depth - 1);
  }
}

function lookUpForSiblingElement(element, selector, depth = 10) {
  if (depth === 0) {
    return null;
  }

  if (element.querySelector(selector)) {
    return element.querySelector(selector);
  } else {
    return lookUpForSiblingElement(element.parentElement, selector, depth - 1);
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Poll until condition() returns truthy or timeout elapses (default 2 s).
function waitForDom(
  condition: () => boolean,
  timeout = 2000,
  interval = 30
): Promise<boolean> {
  return new Promise((resolve) => {
    const deadline = Date.now() + timeout;
    const check = () => {
      if (condition()) return resolve(true);
      if (Date.now() >= deadline) return resolve(false);
      setTimeout(check, interval);
    };
    check();
  });
}

export async function answerQuestions(answers) {
  var errors = [];

  // Begin overlay (ref-counted) for the duration of answering
  beginOverlay('Answering questions…', { title: 'ComprehendPT' });
  try {
    // Process answers sequentially to avoid race conditions between concurrent DOM writes.
    for (const answer of answers) {
      const error = await answerQuestion(answer['Question'], answer['Answer']);
      if (error) {
        errors.push({
          Question: answer['Question'],
          Answer: answer['Answer'],
          Error: error
        });
      }
    }

    console.error('Errors:', errors);
    return errors;
  } finally {
    endOverlay();
  }
}

// Simple async mutex to serialize question answering operations reliably
class AsyncMutex {
  constructor() {
    this.chain = Promise.resolve();
  }

  async runExclusive(task) {
    let release;
    const willLock = new Promise((resolve) => (release = resolve));
    const previous = this.chain;
    this.chain = previous.then(() => willLock);
    await previous;
    try {
      return await task();
    } finally {
      // @ts-ignore release is always assigned above
      release();
    }
  }
}

const questionMutex = new AsyncMutex();
export async function answerQuestion(
  question,
  answer,
  replyType = 'overwrite',
  totalWaitTime = 0
) {
  if (!questionInteractions[question]) {
    console.error(`Question not found: "${question}"`);
    return 'Question not found';
  }
  // function answerQuestion(question, answer) {
  /*
       Description: This function will take the answer and populate the question
       
       Args: 
           question: The question in question
           answer: The answer to the question
      
       Return: null if success otherwise a string with the error message
       */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.log(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // Begin overlay ref-count and queue with this specific question
  beginOverlay('Answering questions…', { title: 'ComprehendPT' });
  pushQuestionToQueue(String(question));

  // Delegate flex questions to the flex handler (it manages its own mutex)
  if (questionInteractions[question]['Type'] === 'Flex') {
    try {
      const flexErrors = await answerFlexQuestion(question, answer, replyType);
      applyAnswerToQuestionCache(question, answer, replyType, flexErrors);
      return true;
    } finally {
      removeQuestionFromQueue(String(question));
      endOverlay();
    }
  }

  return questionMutex.runExclusive(async () => {
    // If it is a short answer or long answer, just populate the input
    if (questionInteractions[question]['Input']) {
      try {
        await questionInteractions[question]['Input'](answer, replyType);
        applyAnswerToQuestionCache(question, answer, replyType, null);
        return true;
      } finally {
        removeQuestionFromQueue(String(question));
        endOverlay();
      }
    }

    // Check if the answer is a list of answers or just a single answer
    if (Array.isArray(answer)) {
      // Choose All
      var notFoundAnswers = [];
      for (var i = 0; i < answer.length; i++) {
        if (answer[i] in questionInteractions[question]['Choices']) {
          console.log('Answer found ' + answer[i]);
          await questionInteractions[question]['Choices'][answer[i]](true);
        } else {
          notFoundAnswers.push(answer[i]);
        }
      }
      // Record the choices that were actually clicked, even if others were not found.
      applyAnswerToQuestionCache(
        question,
        answer.filter((a) => !notFoundAnswers.includes(a)),
        replyType,
        null
      );
      try {
        if (notFoundAnswers.length > 0) {
          console.error(
            'The following answers were not found: ' + notFoundAnswers
          );
          return 'The following answers were not found: ' + notFoundAnswers;
        } else {
          return true;
        }
      } finally {
        removeQuestionFromQueue(String(question));
        endOverlay();
      }
    } else {
      // Multiple Choice
      try {
        if (answer in questionInteractions[question]['Choices']) {
          await questionInteractions[question]['Choices'][answer]();
          applyAnswerToQuestionCache(question, answer, replyType, null);
          return true;
        } else {
          console.error('Answer not found');
          return 'Answer not found';
        }
      } finally {
        removeQuestionFromQueue(String(question));
        endOverlay();
      }
    }
  });
}

/*
 * Keep the getQuestions cache in step with what we just wrote into the note.
 *
 * getQuestions deliberately keeps serving cachedQuestions for 10s after the last answer
 * lands (removeQuestionFromQueue refreshes the TTL) because a rescrape while fills are
 * still in flight clobbers them — and the answer queue does briefly empty mid-Comprehend,
 * so the queue-length guard alone is not enough. The cache was never updated to match
 * those writes, so callers reading the note straight after a Comprehend — Compliance
 * scoring in particular, which runs ~1s later — got the pre-fill snapshot back.
 *
 * Additive only, on purpose: a field is updated only where its write actually landed, and
 * a blank/skipped value never clears an entry.
 */
function applyAnswerToQuestionCache(question, answer, replyType, flexErrors) {
  try {
    const entry = cachedQuestions?.questions?.find(
      (q) => q.Question === question
    );
    if (!entry) return;

    // Mirror the DOM writers: they no-op on empty/placeholder values, so the cache must too.
    const skip = (val) =>
      typeof val !== 'string' ||
      !val.trim() ||
      SKIP_VALUES.has(val.trim().toLowerCase());
    const write = (target, key, val) => {
      const previous = target[key];
      target[key] =
        replyType === 'append' && previous
          ? `${previous}\n${val.trim()}`
          : val.trim();
    };

    if (entry.Type !== 'Flex') {
      const val = Array.isArray(answer) ? answer.join(', ') : answer;
      if (!skip(val)) write(entry, 'ExistingNote', val);
      return;
    }

    // answerFlexQuestion reports failures as "field" or "field.subField".
    const failed = new Set(
      (Array.isArray(flexErrors) ? flexErrors : [])
        .map((e) => e?.Field)
        .filter(Boolean)
    );

    // The answer and the cached Structure are parallel trees — walk them together.
    // Structure stores sanitized keys; the answer uses the keys the fill used.
    const walk = (node, value, path) => {
      if (failed.has(path)) return;
      if (typeof value === 'string') {
        // Multi-select dropdowns are not scraped into Existing Value.
        if (
          typeof node?.Description === 'string' &&
          node.Description.includes("'$'")
        ) {
          return;
        }
        // answerFlexQuestion applies each '$'-separated part in turn, so under
        // 'overwrite' only the last part survives in the note. Mirror that loop.
        for (const part of value.split('$')) {
          if (!skip(part)) write(node, 'Existing Value', part);
        }
        return;
      }
      if (!value || typeof value !== 'object') return;
      for (const [key, child] of Object.entries(value)) {
        const target = node?.[sanitizeFieldName(key)] ?? node?.[key];
        if (target) walk(target, child, path ? `${path}.${key}` : key);
      }
    };
    walk(entry.Structure || {}, answer, '');
  } catch (error) {
    // Never let cache bookkeeping break an answer that already landed in the note.
    console.error('Error updating question cache after answer:', error);
  }
}


async function answerFlexQuestion(
  question,
  answer,
  replyType = 'overwrite',
  totalWaitTime = 0
) {
  return questionMutex.runExclusive(async () => {
    try {
      const errors = [];
      const questionObject = questionInteractions[question];
      const structure = questionObject?.Structure || {};

      for (const [fieldKey, fieldValue] of Object.entries(answer)) {
        const field = structure[fieldKey];

        if (!field) {
          console.warn(`⚠️ Field "${fieldKey}" not found in structure.`);
          errors.push({
            Field: fieldKey,
            Error: 'Field not found in Structure'
          });
          continue;
        }

        // Detect if this field is a group (nested) or single (flat)
        const isGroup =
          typeof fieldValue === 'object' && !('Input' in fieldValue);

        if (isGroup) {
          // Handle nested structure: e.g. Flowsheet["Ball Squeeze"]["Sets"] = "2"
          for (const [subFieldKey, value] of Object.entries(fieldValue)) {
            const sub = field[subFieldKey];

            if (!sub?.Input) {
              errors.push({
                Field: `${fieldKey}.${subFieldKey}`,
                Error: 'Input function not found'
              });
              continue;
            }

            const values =
              typeof value === 'string' ? value.split('$') : [value];
            for (const val of values) {
              try {
                if (/(Functional Goal).*(PLOF|CLOF|Goal)/.test(subFieldKey)) {
                  await sleep(200);
                }
                await sub.Input(val, replyType);
              } catch (error) {
                errors.push({
                  Field: `${fieldKey}.${subFieldKey}`,
                  Error: `Error applying value: ${error.message}`
                });
              }
            }
          }
          // The app closes each section as the next one opens, often inside a
          // single poll interval, so a section written here may never be
          // swept. Read it now while it is still open — otherwise
          // updatePostWriteBaseline drops these paths and we lose AI
          // attribution for exactly the fields this pipeline exists to track.
          if (isNoteCaptureEnabled()) sweepIntoCaptureCache();
        } else {
          // Handle flat structure: e.g. Flowsheet["Procedure"] = "Ball squeeze exercise"
          if (!field?.Input) {
            errors.push({
              Field: fieldKey,
              Error: 'Input function not found'
            });
            continue;
          }

          const values =
            typeof fieldValue === 'string'
              ? fieldValue.split('$')
              : [fieldValue];
          for (const val of values) {
            try {
              await field.Input(val, replyType);
            } catch (error) {
              errors.push({
                Field: fieldKey,
                Error: `Error applying value: ${error.message}`
              });
            }
          }
          // Same reason as the grouped branch above: capture it while it's open.
          if (isNoteCaptureEnabled()) sweepIntoCaptureCache();
        }
      }

      return errors.length > 0 ? errors : null;
    } catch (error) {
      console.error('Unexpected error:', error);
      return {
        Question: question,
        Error: `Unexpected error: ${error.message}`
      };
    }
  });
}

function getExistingNote(value) {
  if (typeof value !== 'string') {
    return null;
  }

  let val = value?.trim();
  return val === '' ? null : val;
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}

/* Start the Blur Overlay */
const OVERLAY_ID = 'BlurOverlay';
const ACTIVE_CLASS = 'blur-overlay-active';
const CSS_ID = 'BlurOverlayStyles';

let overlayEl, msgEl, closeBtn;
let usingBackdrop = true;
let lastFocused = null;

function injectStyles() {
  if (document.getElementById(CSS_ID)) return;
  const style = document.createElement('style');
  style.id = CSS_ID;
  style.textContent = `
     /* Root toggle classes */
     html.${ACTIVE_CLASS} { overflow: hidden; }
     body.${ACTIVE_CLASS} { overflow: hidden; }
  
     /* Fallback blur for browsers without backdrop-filter */
     html.${ACTIVE_CLASS} body > *:not(#${OVERLAY_ID}) {
       filter: blur(var(--blur-overlay-amount, 8px)) saturate(0.9);
       pointer-events: none;
       user-select: none;
     }
  
     /* Overlay container */
     #${OVERLAY_ID} {
       position: fixed;
       inset: 0;
       /* Brand color (tealish green) */
       --brand-color: rgb(107, 216, 136);
       z-index: 2147483647;
       display: none;
       align-items: center;
       justify-content: center;
       padding: 24px;
     }
  
     /* When visible */
     #${OVERLAY_ID}[data-open="true"] { display: flex; }
  
     /* Backdrop blur (preferred) */
     #${OVERLAY_ID}::before {
       content: "";
       position: absolute;
       inset: 0;
       background: rgba(0,0,0,0.25);
       backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
       -webkit-backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
     }
  
     /* Message card */
     #${OVERLAY_ID} .blur-msg {
       position: relative;
       max-width: min(720px, 90vw);
       width: max(320px, 40vw);
       background: rgba(255,255,255,0.9);
       border-radius: 16px;
       box-shadow: 0 10px 30px rgba(0,0,0,0.2);
       padding: 20px 24px;
       font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
       color: #111;
       backdrop-filter: blur(2px);
     }
  
     #${OVERLAY_ID} .blur-msg h2 {
       margin: 0 0 10px 0;
       font-size: 18px;
       font-weight: 600;
       color: var(--brand-color);
     }
  
     #${OVERLAY_ID} .blur-msg .blur-text {
       line-height: 1.5;
       font-size: 15px;
       white-space: pre-wrap;
     }
  
     /* Close (X) in top-right of viewport */
     #${OVERLAY_ID} .blur-close {
       position: fixed;
       top: 12px;
       right: 12px;
       appearance: none;
       border: none;
       background: var(--brand-color);
       color: #fff;
       width: 36px;
       height: 36px;
       border-radius: 10px;
       font-size: 20px;
       line-height: 36px;
       text-align: center;
       cursor: pointer;
       box-shadow: 0 4px 12px rgba(0,0,0,0.25);
     }
     #${OVERLAY_ID} .blur-close:hover { background: #00937f; }
     #${OVERLAY_ID} .blur-close:focus { outline: 2px solid #fff; outline-offset: 2px; }
   `;
  document.head.appendChild(style);
}

function createOverlay() {
  if (document.getElementById(OVERLAY_ID)) {
    overlayEl = document.getElementById(OVERLAY_ID);
    msgEl = overlayEl.querySelector('.blur-text');
    closeBtn = overlayEl.querySelector('.blur-close');
    return;
  }
  overlayEl = document.createElement('div');
  overlayEl.id = OVERLAY_ID;
  overlayEl.setAttribute('aria-hidden', 'true');

  const card = document.createElement('div');
  card.className = 'blur-msg';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-live', 'polite');

  const title = document.createElement('h2');
  title.textContent = 'Notice';

  msgEl = document.createElement('div');
  msgEl.className = 'blur-text';
  msgEl.textContent = '';

  closeBtn = document.createElement('button');
  closeBtn.className = 'blur-close';
  closeBtn.setAttribute('type', 'button');
  closeBtn.setAttribute('aria-label', 'Close overlay');
  closeBtn.textContent = '×';

  card.appendChild(title);
  card.appendChild(msgEl);

  overlayEl.appendChild(card);
  overlayEl.appendChild(closeBtn);
  document.body.appendChild(overlayEl);

  closeBtn.addEventListener('click', hide);
  document.addEventListener('keydown', (e) => {
    if (overlayEl.dataset.open === 'true' && e.key === 'Escape') hide();
  });

  // If user clicks the dim area (not the card), do nothing (X/ESC only)
  overlayEl.addEventListener('click', (e) => {
    const clickedInsideCard = e.target === card || card.contains(e.target);
    const clickedClose = e.target === closeBtn;
    if (!clickedInsideCard && !clickedClose) {
      // no-op; keep modal open unless using X/ESC
    }
  });
}

function ensureSupport() {
  usingBackdrop =
    CSS.supports('backdrop-filter: blur(8px)') ||
    CSS.supports('-webkit-backdrop-filter: blur(8px)');
}

function show(message = '', opts = {}) {
  injectStyles();
  createOverlay();
  ensureSupport();

  // Options
  const {
    title = 'Notice',
    blurAmount = 8,
    textColor,
    background,
    width // e.g. '600px' or '50vw'
  } = opts;

  // Apply styles via CSS vars and inline as needed
  overlayEl.style.setProperty('--blur-overlay-amount', String(blurAmount));
  const card = overlayEl.querySelector('.blur-msg');
  if (textColor) card.style.color = textColor;
  if (background) card.style.background = background;
  if (width) card.style.width = width;

  overlayEl.querySelector('h2').textContent = title;
  msgEl.textContent = message ?? '';

  // Toggle classes depending on support
  document.documentElement.classList.add(ACTIVE_CLASS);
  document.body.classList.add(ACTIVE_CLASS);

  if (!usingBackdrop) {
    // Fallback: blur page content via CSS filter on everything but the overlay
    // Already handled by the CSS rule when ACTIVE_CLASS is present.
  }

  // Control visibility of close button (always visible with current UX)
  closeBtn.style.display = '';

  // Open
  overlayEl.dataset.open = 'true';
  overlayEl.setAttribute('aria-hidden', 'false');

  // Focus management
  lastFocused =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  closeBtn.focus();

  return true;
}

function update(message = '') {
  if (!overlayEl) createOverlay();
  msgEl.textContent = message ?? '';
  return true;
}

function hide() {
  if (!overlayEl) return false;

  overlayEl.dataset.open = 'false';
  overlayEl.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove(ACTIVE_CLASS);
  document.body.classList.remove(ACTIVE_CLASS);

  // Restore focus
  if (lastFocused && document.contains(lastFocused)) {
    try {
      lastFocused.focus();
    } catch {}
  }
  lastFocused = null;
  return true;
}

function isVisible() {
  return !!(overlayEl && overlayEl.dataset.open === 'true');
}

// // Public API -> Not used globally, only used in this file...
// window.BlurOverlay = { show, update, hide, isVisible };
/* End the Blur Overlay */

/* Overlay ref-count helpers to avoid cross-call hide races */
let __overlayRefCount = 0;

function beginOverlay(message, opts) {
  __overlayRefCount++;
  if (!isVisible()) {
    show(message, opts);
  } else {
    update(message);
  }
  return __overlayRefCount;
}

function endOverlay() {
  __overlayRefCount = Math.max(0, __overlayRefCount - 1);
  if (__overlayRefCount === 0 && isVisible()) {
    hide();
  }
}

// Queue tracking for per-question answering overlay messaging
let __overlayAnswerQueue = [];
function formatAnswerQueueMessage() {
  const total = __overlayAnswerQueue.length;
  const header = 'Finishing your note… This should only take a few seconds.';
  if (total === 0) return header;

  const current = __overlayAnswerQueue[0];
  const nextItems = __overlayAnswerQueue.slice(1);
  const nextPreview = nextItems.slice(0, 3);
  const remaining = Math.max(0, nextItems.length - nextPreview.length);

  let message = header + `\nCurrently working on: ${current}`;
  if (nextPreview.length > 0) {
    const list = nextPreview.join(', ');
    message +=
      `\nUp next: ${list}` + (remaining > 0 ? `, and ${remaining} more` : '');
  }
  return message;
}
function pushQuestionToQueue(questionLabel) {
  if (!questionLabel) return;
  if (!__overlayAnswerQueue.includes(questionLabel)) {
    __overlayAnswerQueue.push(questionLabel);
  }
  if (isVisible()) update(formatAnswerQueueMessage());
}
function removeQuestionFromQueue(questionLabel) {
  const idx = __overlayAnswerQueue.indexOf(questionLabel);
  if (idx !== -1) __overlayAnswerQueue.splice(idx, 1);
  if (isVisible()) update(formatAnswerQueueMessage());
  lastGetQuestionsAt = Date.now();
}
